import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  GUEST_PREFIX,
  clarifications,
  describeAssignments,
  errorsOnly,
  formatMoney,
  itemsTotal,
  joinNames,
  parseSplit,
  settle,
  structureReceipt,
  validateReceipt,
  type Person,
  type Receipt,
} from '@bill/core';
import { getProvider } from '../app/provider.js';
import { useKeyboardInset } from '../app/keyboard.js';
import { autoTurns, useRotatedPhoto } from '../app/rotate.js';
import { useSpeech } from '../app/speech.js';
import {
  DEFAULT_NAME,
  SPEAKER_ID,
  addTurn,
  currentContext,
  deriveSplit,
  editReceipt,
  finalResolve,
  historyFor,
  photoUrl,
  reset,
  setReceipt,
  splitItems,
  update,
  useSession,
  wipeAll,
} from '../app/store.js';
import { encodeForUpload } from '../image.js';
import { Avatar } from '../components/Avatar.jsx';
import { Icon, Logo } from '../components/Icon.jsx';
import { PeoplePicker } from '../components/PeoplePicker.jsx';
import { ReceiptEditor } from '../components/ReceiptEditor.jsx';
import { ShareBar, SplitCards } from '../components/SplitResult.jsx';

/**
 * The whole app is one conversation.
 *
 * The user sends a photo, then sentences; the app answers. Every app message
 * is generated by the engine from state it already has — what it read, the
 * arithmetic check, a readback of what a sentence meant, the questions for
 * what is still unknown, the split itself — so nothing the app says can
 * disagree with the numbers, and none of it costs a model call. Only the
 * photo and the user's own messages go to the model.
 *
 * The composer never closes. After the result, a correction is one more
 * message; the model sees the whole conversation so far and the later
 * sentence wins. "New bill" starts a fresh conversation.
 */
export function Chat(): React.ReactElement {
  const session = useSession();
  const { receipt, image, hasPhoto, confirmed, readError, myName, turns, headCount, manual, renames, extrasRule } = session;
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  /** What the user just sent, shown at once while the model is still thinking. */
  const [pending, setPending] = useState<string | null>(null);
  const [reading, setReading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [viewer, setViewer] = useState(false);
  const speech = useSpeech();
  useKeyboardInset();
  const draftBeforeSpeech = useRef('');
  const composer = useRef<HTMLTextAreaElement>(null);
  const end = useRef<HTMLDivElement>(null);
  const abort = useRef<AbortController | null>(null);

  useEffect(() => () => abort.current?.abort(), []);

  useEffect(() => {
    if (!speech.listening && speech.transcript === '') return;
    const prefix = draftBeforeSpeech.current;
    setDraft(prefix ? `${prefix.trimEnd()} ${speech.transcript}` : speech.transcript);
  }, [speech.transcript, speech.listening]);

  const outcome = confirmed ? deriveSplit(session) : null;
  const ok = outcome?.status === 'ok' ? outcome : null;
  const resolved = ok ? finalResolve(session, ok) : null;
  const open = ok && resolved && receipt && !ok.askHeadCount ? clarifications(resolved, splitItems(receipt)) : [];
  const complete = Boolean(ok && resolved && !ok.askHeadCount && open.length === 0);
  const issues = receipt ? validateReceipt(receipt) : [];
  const receiptErrors = errorsOnly(issues).length;

  // Keep the newest message in view.
  useEffect(() => {
    end.current?.scrollIntoView({ block: 'end' });
  }, [turns.length, busy, pending, reading, hasPhoto, receipt, confirmed, editing, headCount, open.length, complete]);

  // --- actions ---------------------------------------------------------------

  const onFile = async (file: File | undefined): Promise<void> => {
    if (!file) return;
    setReading(true);
    setEditing(false);
    update({ hasPhoto: true, readError: undefined });
    abort.current = new AbortController();
    try {
      const encoded = await encodeForUpload(file);
      const result = await structureReceipt(
        getProvider(),
        { kind: 'image', base64: encoded.base64, mimeType: encoded.mimeType },
        { retries: 0, signal: abort.current.signal },
      );
      if (result.status === 'failed') {
        update({ readError: result.errors.join(' ') || 'nothing readable came back' });
        return;
      }
      setReceipt(result.receipt, { base64: encoded.base64, mimeType: encoded.mimeType, width: encoded.width, height: encoded.height });
    } catch (caught) {
      if (abort.current?.signal.aborted) return;
      update({ readError: caught instanceof Error ? caught.message : String(caught) });
    } finally {
      setReading(false);
    }
  };

  const send = async (): Promise<void> => {
    const text = draft.trim();
    const context = currentContext(session);
    if (!text || !context || busy || !confirmed) return;
    setBusy(true);
    setPending(text);
    setDraft('');
    if (composer.current) composer.current.style.height = 'auto';
    speech.stop();
    try {
      const history = historyFor(session);
      const result = await parseSplit(getProvider(), context, text, history ? { history } : {});
      addTurn(result.status === 'failed' ? { text, error: result.errors.join(' ') } : { text, reply: result.reply });
      // "Tudor (me)" — the user just told us their name.
      if (result.status === 'ok' && result.speakerAlias && (myName.trim() === '' || myName === DEFAULT_NAME)) {
        update({ myName: result.speakerAlias });
      }
    } catch (caught) {
      addTurn({ text, error: caught instanceof Error ? caught.message : String(caught) });
    } finally {
      setPending(null);
      setBusy(false);
    }
  };

  const toggleMic = (): void => {
    if (speech.listening) {
      speech.stop();
    } else {
      draftBeforeSpeech.current = draft;
      speech.start();
    }
  };

  // --- transcript ------------------------------------------------------------

  const items = receipt?.items ?? [];
  /** The lines as the split sees them: "2× Long Island" is two units here. */
  const units = receipt ? splitItems(receipt) : [];
  const currency = receipt?.currency ?? 'XXX';
  const people: Person[] = ok?.people ?? [{ id: SPEAKER_ID, name: myName }];
  const lastReplyIndex = turns.map((turn) => Boolean(turn.reply)).lastIndexOf(true);
  const messages: ReactNode[] = [];

  const photoButtons = (
    <div className="stack">
      <label className="button primary big">
        <input type="file" accept="image/*" capture="environment" hidden aria-label="Take a photo" onChange={(event) => void onFile(event.target.files?.[0])} />
        <Icon name="camera" /> Take a photo
      </label>
      <label className="button big">
        <input type="file" accept="image/*" hidden aria-label="Choose from gallery" onChange={(event) => void onFile(event.target.files?.[0])} />
        <Icon name="image" /> Choose from gallery
      </label>
    </div>
  );

  messages.push(
    <Bubble key="hi" from="app">
      <p className="greeting">Snap the bill.</p>
      <p>I'll read it, then you tell me who had what, like you would a friend.</p>
      {!hasPhoto && photoButtons}
    </Bubble>,
  );

  const rawPhoto = photoUrl(image);
  const upright = image?.width && image.height ? autoTurns(image.width, image.height) : 0;
  const photo = useRotatedPhoto(rawPhoto, upright);
  if (hasPhoto) {
    messages.push(
      <Bubble key="photo" from="user" tone="photo">
        {photo ? (
          <button type="button" className="photo-open" aria-label="Open the photo" onClick={() => setViewer(true)}>
            <img src={photo} alt="The receipt" />
          </button>
        ) : (
          <span className="photo-placeholder">
            <Icon name="receipt" /> Photo of the bill
          </span>
        )}
      </Bubble>,
    );
  }

  if (reading) {
    messages.push(
      <Bubble key="reading" from="app">
        <Typing label="Reading the receipt" />
      </Bubble>,
    );
  }

  if (readError && !reading) {
    messages.push(
      <Bubble key="readerror" from="app" tone="error">
        <p>I couldn't read that photo. A sharper, flatter shot usually does it.</p>
        <p className="small">{readError}</p>
        {photoButtons}
      </Bubble>,
    );
  }

  if (receipt && !reading) {
    messages.push(
      <Bubble key="receipt" from="app" wide>
        <div className="receipt-head">
          <Icon name="receipt" size={18} />
          <span className="receipt-merchant">{receipt.merchant ?? 'Receipt'}</span>
          <span className="muted small">
            {items.length} {items.length === 1 ? 'item' : 'items'}
          </span>
        </div>
        {!confirmed && <p className="small muted">Here's what I read. Check it before we split.</p>}
        {editing ? (
          <ReceiptEditor receipt={receipt} previewUrl={photo} onChange={editReceipt} />
        ) : (
          <ReceiptSummary receipt={receipt} />
        )}
        {!editing && receiptErrors > 0 && (
          <p className="small warn">
            {receiptErrors === 1 ? 'One thing' : `${receiptErrors} things`} don't add up. Tap Edit to fix.
          </p>
        )}
        <div className="share-row">
          {!confirmed && (
            <button
              type="button"
              className="button primary"
              disabled={receiptErrors > 0}
              onClick={() => {
                setEditing(false);
                update({ confirmed: true });
                window.setTimeout(() => composer.current?.focus(), 50);
              }}
            >
              <Icon name="check" /> Looks right
            </button>
          )}
          <button type="button" className="button" onClick={() => setEditing(!editing)}>
            {editing ? <Icon name="check" /> : <Icon name="edit" />}
            {editing ? 'Done' : 'Edit'}
          </button>
        </div>
      </Bubble>,
    );
  }

  if (confirmed && receipt) {
    messages.push(
      <Bubble key="confirmed" from="user">
        <span className="with-icon">
          <Icon name="check" size={18} /> Looks right
        </span>
      </Bubble>,
      <Bubble key="ask" from="app">
        <p>Who had what? Say it like you would to a friend.</p>
      </Bubble>,
    );
  }

  turns.forEach((turn, index) => {
    messages.push(
      <Bubble key={`u${index}`} from="user">
        {turn.text}
      </Bubble>,
    );
    if (turn.error) {
      messages.push(
        <Bubble key={`e${index}`} from="app" tone="error">
          <p>I couldn't work that out. Try naming people and dishes the way they appear on the bill.</p>
          <p className="small">{turn.error}</p>
        </Bubble>,
      );
    } else if (turn.reply && index === lastReplyIndex && ok && resolved) {
      const groups = describeAssignments(resolved, units, people, SPEAKER_ID);
      messages.push(
        <Bubble key={`r${index}`} from="app">
          <p>{index === 0 ? 'Got it.' : 'Updated.'}</p>
          <ul className="readback">
            {groups.map((group) => (
              <li key={group.who} className={group.holders.length === 0 ? 'muted' : ''}>
                <strong>{group.who}</strong>: {group.items.join(', ')}
              </li>
            ))}
          </ul>
        </Bubble>,
      );
    } else if (turn.reply) {
      messages.push(
        <Bubble key={`r${index}`} from="app">
          Noted.
        </Bubble>,
      );
    }
  });

  // The turn lands one render before `pending` clears; never show both.
  if (pending !== null && turns.at(-1)?.text !== pending) {
    messages.push(
      <Bubble key="pending" from="user">
        {pending}
      </Bubble>,
    );
  }
  if (busy) {
    messages.push(
      <Bubble key="busy" from="app">
        <Typing label="Thinking" />
      </Bubble>,
    );
  }

  if (ok && ok.askHeadCount) {
    messages.push(<HeadCountQuestion key="headcount" named={people.map((person) => person.name)} onAnswer={(n) => update({ headCount: n })} />);
  } else if (ok && headCount !== undefined && ok.reply.everyone && ok.reply.groupSize === undefined) {
    messages.push(
      <Bubble key="headcount-q" from="app">
        How many of you were there?
      </Bubble>,
      <Bubble key="headcount-a" from="user">
        {headCount} of us
      </Bubble>,
    );
  }

  for (const question of open) {
    const item = units.find((candidate) => candidate.id === question.itemId);
    messages.push(
      <Bubble key={`q-${question.itemId}`} from="app">
        <p>
          Who had the <strong>{question.itemName}</strong>
          {item ? <span className="muted"> ({formatMoney(item.lineTotal, currency)})</span> : null}?
        </p>
        <PeoplePicker people={people} selected={manual[question.itemId] ?? []} onChange={(selected) => update({ manual: { ...manual, [question.itemId]: selected } })} />
      </Bubble>,
    );
  }

  if (complete && ok && resolved && receipt) {
    let body: ReactNode;
    try {
      const settlement = settle({ ...receipt, items: units }, ok.people, resolved, { extrasRule });
      const guests = ok.people.filter((person) => person.name.startsWith(`${GUEST_PREFIX} `)).length;
      body = (
        <>
          <p>Here's the split.</p>
          <SplitCards settlement={settlement} items={units} speakerId={SPEAKER_ID} extrasRule={extrasRule} onExtrasRule={(rule) => update({ extrasRule: rule })} />
          <p className="sum-check">
            <Icon name="check" size={16} /> Adds up to {formatMoney(settlement.grandTotal, currency)}
            {receipt.total !== undefined && settlement.grandTotal === receipt.total ? ', the bill total.' : '.'}
          </p>
          {ok.people.length > 1 && (
            <details className="names" open={guests > 0}>
              <summary>{guests > 0 ? `Name the ${guests === 1 ? 'guest' : 'guests'}` : 'Fix a name'}</summary>
              <ul className="people">
                {ok.people
                  .filter((person) => person.id !== SPEAKER_ID)
                  .map((person) => (
                    <li key={person.id} className="person">
                      <Avatar name={renames[person.id] ?? person.name} size={28} />
                      <input
                        type="text"
                        className="person-edit"
                        aria-label={`Name for ${person.name}`}
                        value={renames[person.id] ?? person.name}
                        autoCapitalize="words"
                        onChange={(event) => update({ renames: { ...renames, [person.id]: event.target.value } })}
                      />
                    </li>
                  ))}
              </ul>
            </details>
          )}
          <ShareBar settlement={settlement} items={units} speakerId={SPEAKER_ID} />
        </>
      );
    } catch (error) {
      body = <p className="small">{error instanceof Error ? error.message : String(error)}</p>;
    }
    messages.push(
      <Bubble key="done" from="app" tone="done" wide>
        {body}
      </Bubble>,
      <Bubble key="nudge" from="app">
        Something off? Just tell me and I'll redo it.
      </Bubble>,
    );
  }

  const placeholder = !receipt
    ? 'Snap the bill first'
    : !confirmed
      ? 'Check the receipt first'
      : complete
        ? 'Anything to change?'
        : turns.length === 0
          ? 'Who had what?'
          : 'Add or correct something…';

  return (
    <div className="shell">
      <header className="top">
        <span className="brand" aria-hidden="true">
          <Logo />
        </span>
        <span className="title">Bill Splitter</span>
        {(hasPhoto || turns.length > 0) && (
          <button
            type="button"
            className="link newbill"
            onClick={() => {
              abort.current?.abort();
              setEditing(false);
              setDraft('');
              reset();
            }}
          >
            New bill
          </button>
        )}
        <button
          type="button"
          className="link newbill"
          title="Forget everything stored in this browser and start as a first-time visitor"
          onClick={() => {
            if (window.confirm('Reset the app? This forgets your name, the current bill and demo mode.')) wipeAll();
          }}
        >
          Reset
        </button>
      </header>

      <main className="body">
        <div className="chat" role="log" aria-live="polite">
          {messages}
          {speech.error && <p className="hint small warn">{speech.error}</p>}
          <div ref={end} />
        </div>
      </main>

      {viewer && rawPhoto && <PhotoViewer src={rawPhoto} startTurns={upright} onClose={() => setViewer(false)} />}

      <footer className="actions">
        <div className="composer">
          <label className="attach" aria-label="Take a photo">
            <input type="file" accept="image/*" capture="environment" hidden disabled={reading} onChange={(event) => void onFile(event.target.files?.[0])} />
            <Icon name="camera" />
          </label>
          <textarea
            ref={composer}
            rows={1}
            placeholder={placeholder}
            aria-label="Message"
            value={draft}
            disabled={busy || !confirmed}
            onChange={(event) => {
              setDraft(event.target.value);
              event.target.style.height = 'auto';
              event.target.style.height = `${Math.min(event.target.scrollHeight, 128)}px`;
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey && !('ontouchstart' in window)) {
                event.preventDefault();
                void send();
              }
            }}
          />
          {speech.supported && (
            <button
              type="button"
              className={`mic${speech.listening ? ' on' : ''}`}
              aria-pressed={speech.listening}
              aria-label={speech.listening ? 'Stop listening' : 'Speak instead'}
              disabled={busy || !confirmed}
              onPointerDown={(event) => event.preventDefault()}
              onClick={toggleMic}
            >
              {speech.listening ? <Icon name="stop" /> : <Icon name="mic" />}
            </button>
          )}
          <button
            type="button"
            className="send"
            aria-label="Send"
            disabled={busy || !confirmed || draft.trim() === ''}
            onPointerDown={(event) => event.preventDefault()}
            onClick={() => void send()}
          >
            <Icon name="send" />
          </button>
        </div>
      </footer>
    </div>
  );
}

// --- pieces ------------------------------------------------------------------

function Bubble({ from, tone, wide, children }: { from: 'app' | 'user'; tone?: 'error' | 'done' | 'photo'; wide?: boolean; children: ReactNode }): React.ReactElement {
  return <div className={`bubble ${from}${tone ? ` ${tone}` : ''}${wide ? ' wide' : ''}`}>{children}</div>;
}

function Typing({ label }: { label: string }): React.ReactElement {
  return (
    <span className="typing" role="status" aria-label={label}>
      <i />
      <i />
      <i />
    </span>
  );
}

const PREVIEW_LINES = 6;

/** The receipt at a glance: the first few lines, the rest on tap, then the totals. */
function ReceiptSummary({ receipt }: { receipt: Receipt }): React.ReactElement {
  const { currency } = receipt;
  const lines = itemsTotal(receipt);
  const [showAll, setShowAll] = useState(receipt.items.length <= PREVIEW_LINES + 2);
  const shown = showAll ? receipt.items : receipt.items.slice(0, PREVIEW_LINES);
  const hidden = receipt.items.length - shown.length;
  return (
    <div className="receipt-summary">
      <ul>
        {shown.map((item) => (
          <li key={item.id}>
            <span>
              {item.qty > 1 ? `${item.qty}× ` : ''}
              {item.name}
            </span>
            <span className="num">{formatMoney(item.lineTotal, currency)}</span>
          </li>
        ))}
      </ul>
      {hidden > 0 && (
        <button type="button" className="more" onClick={() => setShowAll(true)}>
          Show {hidden} more
        </button>
      )}
      <dl>
        {receipt.subtotal !== undefined && receipt.subtotal !== lines && (
          <>
            <dt>Subtotal</dt>
            <dd>{formatMoney(receipt.subtotal, currency)}</dd>
          </>
        )}
        {receipt.tax !== undefined && receipt.tax !== 0 && (
          <>
            <dt>Tax</dt>
            <dd>{formatMoney(receipt.tax, currency)}</dd>
          </>
        )}
        {receipt.tip !== undefined && receipt.tip !== 0 && (
          <>
            <dt>Tip</dt>
            <dd>{formatMoney(receipt.tip, currency)}</dd>
          </>
        )}
        <dt>Total</dt>
        <dd>
          <strong>{receipt.total !== undefined ? formatMoney(receipt.total, currency) : '—'}</strong>
        </dd>
      </dl>
    </div>
  );
}


/** "You said everyone — how many is that?" A stepper that starts at the named count. */
function HeadCountQuestion({ named, onAnswer }: { named: string[]; onAnswer(n: number): void }): React.ReactElement {
  const [count, setCount] = useState(Math.max(named.length, 2));
  const extra = count - named.length;
  return (
    <Bubble from="app">
      <p>
        How many of you were there? <span className="muted">{named.length > 1 ? `I have ${joinNames(named)}.` : 'Count yourself too.'}</span>
      </p>
      <div className="stepper compact" role="group" aria-label="Number of people">
        <button type="button" aria-label="Fewer" disabled={count <= Math.max(named.length, 1)} onClick={() => setCount(count - 1)}>
          <Icon name="minus" />
        </button>
        <output aria-live="polite">{count}</output>
        <button type="button" aria-label="More" disabled={count >= 30} onClick={() => setCount(count + 1)}>
          <Icon name="plus" />
        </button>
      </div>
      <button type="button" className="button primary" onClick={() => onAnswer(count)}>
        {extra > 0 ? `${count} people, ${extra} unnamed` : `Just the ${count} of us`}
      </button>
      {extra > 0 && <p className="small muted">The unnamed show up as Guest 1{extra > 1 ? ` to ${extra}` : ''}; you can name them at the end.</p>}
    </Bubble>
  );
}

/**
 * Full-screen photo. Long receipts scroll; pinch zoom is the browser's own.
 * Starts upright by the landscape guess; the button turns it further.
 * Rendered into <body> so no stacking context in the page can sit above it.
 */
function PhotoViewer({ src, startTurns, onClose }: { src: string; startTurns: number; onClose(): void }): React.ReactElement {
  const [turns, setTurns] = useState(startTurns);
  const shown = useRotatedPhoto(src, turns);
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);
  return createPortal(
    <div className="viewer" role="dialog" aria-label="Receipt photo" onClick={onClose}>
      <div className="viewer-bar" onClick={(event) => event.stopPropagation()}>
        <button type="button" className="viewer-btn" aria-label="Rotate" onClick={() => setTurns((t) => t + 1)}>
          <Icon name="refresh" />
        </button>
        <button type="button" className="viewer-btn" aria-label="Close" onClick={onClose}>
          <Icon name="x" />
        </button>
      </div>
      <div className="viewer-scroll" onClick={(event) => event.stopPropagation()}>
        {shown && <img src={shown} alt="The receipt, full size" />}
      </div>
    </div>,
    document.body,
  );
}
